import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getNextVersionNumber,
  useCreateQuoteVersion,
  useQuote,
} from "@/hooks/useQuotes";
import { useDuplicateVersionContent } from "@/hooks/useQuoteEntities";
import { QuoteVersionEditor } from "./QuoteVersionEditor";
import type { CustomerContact } from "@/types/finance-entities";

interface QuoteDetailViewProps {
  quoteId: string;
  selectedVersionId?: string;
  basePath: string;
  projectId: string;
  projectNumber?: string;
  projectLanguage: string;
  projectConditions?: string | null;
  projectDefaultVatRate: "CH_8_1" | "NONE";
  projectDefaultDiscountPercent: number;
  eventStartDate?: string;
  eventEndDate?: string;
  planningStartDate?: string;
  planningEndDate?: string;
  customerContact: CustomerContact;
  onBack: () => void;
}

export function QuoteDetailView({
  quoteId,
  selectedVersionId,
  basePath,
  projectId,
  projectNumber,
  projectLanguage,
  projectConditions,
  projectDefaultVatRate,
  projectDefaultDiscountPercent,
  eventStartDate,
  eventEndDate,
  planningStartDate,
  planningEndDate,
  customerContact,
  onBack,
}: QuoteDetailViewProps) {
  const navigate = useNavigate();
  const { data: quote, isLoading } = useQuote(quoteId);
  const createVersion = useCreateQuoteVersion();
  const duplicateContent = useDuplicateVersionContent();
  const hasCreatedInitialVersion = useRef(false);

  const visibleVersions = useMemo(
    () =>
      [...(quote?.versions ?? [])]
        .filter((version) => version.status !== "rejected")
        .sort((a, b) => b.version_number - a.version_number),
    [quote?.versions],
  );

  const fallbackVersions = useMemo(
    () =>
      [...(quote?.versions ?? [])].sort(
        (a, b) => b.version_number - a.version_number,
      ),
    [quote?.versions],
  );

  const createAndOpenVersion = useCallback(
    async (sourceVersionId?: string | null) => {
      if (!quote) return;

      let projectDiscountPercent: number | undefined =
        projectDefaultDiscountPercent;
      let vatRate: "CH_8_1" | "NONE" | undefined = projectDefaultVatRate;
      let generalConditions: string | undefined;

      if (sourceVersionId) {
        const sourceVersion = quote.versions?.find(
          (version) => version.id === sourceVersionId,
        );
        if (sourceVersion) {
          projectDiscountPercent = sourceVersion.project_discount_percent;
          vatRate = sourceVersion.vat_rate as "CH_8_1" | "NONE";
          generalConditions = sourceVersion.general_conditions || undefined;
        }
      }

      const data = await createVersion.mutateAsync({
        quoteId,
        versionNumber: getNextVersionNumber(quote),
        projectDiscountPercent,
        vatRate,
        generalConditions,
      });

      if (sourceVersionId) {
        await duplicateContent.mutateAsync({
          sourceVersionId,
          targetVersionId: data.id,
        });
      }

      navigate(`${basePath}/versions/${data.id}`, { replace: true });
    },
    [
      basePath,
      createVersion,
      duplicateContent,
      navigate,
      projectDefaultDiscountPercent,
      projectDefaultVatRate,
      quote,
      quoteId,
    ],
  );

  useEffect(() => {
    if (!quote || selectedVersionId) return;

    const version = visibleVersions[0] ?? fallbackVersions[0];
    if (version) {
      navigate(`${basePath}/versions/${version.id}`, { replace: true });
      return;
    }

    if (!hasCreatedInitialVersion.current) {
      hasCreatedInitialVersion.current = true;
      void createAndOpenVersion(null);
    }
  }, [
    basePath,
    createAndOpenVersion,
    fallbackVersions,
    navigate,
    quote,
    selectedVersionId,
    visibleVersions,
  ]);

  if (isLoading || (quote && !selectedVersionId)) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Quote not found</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Quotes
        </Button>
      </div>
    );
  }

  if (!selectedVersionId) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <QuoteVersionEditor
      versionId={selectedVersionId}
      quote={quote}
      projectId={projectId}
      projectNumber={projectNumber}
      projectLanguage={projectLanguage}
      projectConditions={projectConditions}
      eventStartDate={eventStartDate}
      eventEndDate={eventEndDate}
      planningStartDate={planningStartDate}
      planningEndDate={planningEndDate}
      customerContact={customerContact}
      projectCustomerName={customerContact.customerName}
      onBack={onBack}
      onCreateVersion={(duplicateFromId) => {
        void createAndOpenVersion(duplicateFromId);
      }}
    />
  );
}
